import React from "react";
import { formatExpiry, formatDate } from "../../Helpers/SuppressionDuration";

/**
 * Task 9 — grid definitions shared by the Error Suppression and Manage Suppressions pages.
 *
 * The two pages are separate routes but present the same underlying data. Keeping their columns,
 * renderers and grid options in one module is what stops them drifting into two slightly
 * different presentations of the same thing. Neither page should define a column inline.
 */

/** Grid options every suppression grid uses. */
export const suppressionGridProps = {
    defaultColDef: { sortable: true, resizable: true, filter: false },
    domLayout: "autoHeight",
    pagination: true,
    paginationPageSize: 20,
    paginationPageSizeSelector: [10, 20, 50],
    overlayNoRowsTemplate: '<span class="ag-overlay-no-rows-center">Nothing to show</span>',
};

// `info` is retained deliberately even though B3 keeps Info-severity errors out of the list: it is
// also the fallback style for an unrecognised severity (see SeverityBadge below), so removing it
// would leave an unknown value with no styling at all.
const SEVERITY_STYLES = {
    error: { background: "#b034343a", color: "#8c2626", border: "1px solid #b03434" },
    warning: { background: "#fffaf0", color: "#8a6100", border: "1px solid #f2a900" },
    info: { background: "#eef4fa", color: "#23496d", border: "1px solid #9ab6d3" },
};

/** D4 — the machine reason a suppression ended, in words. */
const REMOVAL_REASON_LABELS = {
    Expired: "Expired",
    RemovedByUser: "Removed by you",
    KnownErrorDeactivated: "Error withdrawn",
};

function SeverityBadge({ value }) {
    const style = SEVERITY_STYLES[(value || "").toLowerCase()] || SEVERITY_STYLES.info;
    return (
        <span
            style={{
                ...style,
                borderRadius: 10,
                padding: "1px 10px",
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: "nowrap",
            }}
        >
            {value}
        </span>
    );
}

/**
 * Suppression state for one known error across the organization's jobs.
 *
 * Three states, not two. The specification describes "not suppressed" and "suppressed on all
 * jobs", but the middle case — suppressed on some — is both reachable and the most informative,
 * so it is spelled out.
 *
 * B4 CONFIRMED 2026-08-17: the client agreed this wording, and agreed that the Suppress button stays
 * available in the middle state so more jobs can be added (see the Actions column below), with
 * removal handled per job in the details modal. Settled — do not re-open.
 */
function SuppressionStateCell({ data }) {
    const { suppressedJobCount = 0, totalJobCount = 0, earliestExpiry } = data;

    if (suppressedJobCount === 0) {
        return <span style={{ color: "#6b7c8d" }}>Not suppressed</span>;
    }

    const all = suppressedJobCount >= totalJobCount && totalJobCount > 0;
    const label = all
        ? `Suppressed on all ${totalJobCount} jobs`
        : `Suppressed on ${suppressedJobCount} of ${totalJobCount} jobs`;

    return (
        <span>
            <span style={{ color: "#1a7a3c", fontWeight: 600 }}>{label}</span>
            <span style={{ color: "#6b7c8d", marginLeft: 6, fontSize: 12 }}>
                ({earliestExpiry ? `until ${formatExpiry(earliestExpiry)}` : "indefinite"})
            </span>
        </span>
    );
}

function ActionBar({ children }) {
    return <div style={{ display: "flex", gap: 6, alignItems: "center", height: "100%" }}>{children}</div>;
}

/**
 * Columns for the known error catalog on the Error Suppression page.
 *
 * canChange comes from the access level the API reports. A View-only caller still sees every
 * row and can open the details modal; only the buttons that mutate anything disappear.
 */
export function knownErrorColumns({ onViewDetails, onSuppress, onRemoveAll, canChange }) {
    return [
        { field: "errorId", headerName: "Error ID", width: 120 },
        { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
        { field: "description", headerName: "Description", flex: 2, minWidth: 280, tooltipField: "description" },
        {
            field: "severity",
            headerName: "Severity",
            width: 120,
            cellRenderer: (params) => <SeverityBadge value={params.value} />,
        },
        {
            headerName: "Suppression",
            field: "suppressedJobCount",
            width: 280,
            cellRenderer: (params) => <SuppressionStateCell data={params.data} />,
        },
        {
            headerName: "Actions",
            sortable: false,
            width: 250,
            cellRenderer: (params) => {
                const row = params.data;
                const suppressedOnAll =
                    row.totalJobCount > 0 && row.suppressedJobCount >= row.totalJobCount;
                return (
                    <ActionBar>
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => onViewDetails(row)}
                        >
                            View details
                        </button>
                        {canChange &&
                            (suppressedOnAll ? (
                                <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => onRemoveAll(row)}
                                >
                                    Remove
                                </button>
                            ) : (
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => onSuppress(row)}
                                >
                                    Suppress
                                </button>
                            ))}
                    </ActionBar>
                );
            },
        },
    ];
}

/** Columns for the Manage Suppressions page — one row per (job, known error) rule. */
export function suppressionColumns({ onAmend, onRemove, canChange }) {
    return [
        { field: "errorId", headerName: "Error ID", width: 120 },
        { field: "knownErrorName", headerName: "Name", flex: 2, minWidth: 240 },
        { field: "jobName", headerName: "Job", flex: 2, minWidth: 220 },
        {
            field: "severity",
            headerName: "Severity",
            width: 120,
            cellRenderer: (params) => <SeverityBadge value={params.value} />,
        },
        {
            field: "createdAt",
            headerName: "Suppressed on",
            width: 150,
            valueFormatter: (params) => formatDate(params.value),
        },
        {
            field: "suppressedUntil",
            headerName: "Expires",
            width: 150,
            // formatExpiry renders "Indefinite" for a null expiry, which is the spec's wording.
            valueFormatter: (params) => formatExpiry(params.value),
        },
        { field: "suppressedByEmail", headerName: "Suppressed by", flex: 1, minWidth: 200 },
        // C5 — the justification. Rendered as a plain grid value, so ag-grid escapes it; never
        // switch this to a cellRenderer using dangerouslySetInnerHTML.
        {
            field: "note",
            headerName: "Reason",
            flex: 1,
            minWidth: 180,
            tooltipField: "note",
            valueFormatter: (params) => params.value || "—",
        },
        {
            headerName: "Actions",
            sortable: false,
            width: 190,
            hide: !canChange,
            cellRenderer: (params) => (
                <ActionBar>
                    <button className="btn btn-sm btn-outline-primary" onClick={() => onAmend(params.data)}>
                        Amend
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => onRemove(params.data)}>
                        Remove
                    </button>
                </ActionBar>
            ),
        },
    ];
}

/**
 * D4 — columns for the "Recently ended" view: suppressions that expired or were removed within the
 * visibility window, each of which can be put back.
 *
 * A separate column set rather than a variant of suppressionColumns: the two views answer different
 * questions ("what is suppressed" vs "what stopped being suppressed"), so Expires and Suppressed by
 * give way to Ended on and Reason.
 */
export function endedSuppressionColumns({ onReinstate, canChange }) {
    return [
        { field: "errorId", headerName: "Error ID", width: 120 },
        { field: "knownErrorName", headerName: "Name", flex: 2, minWidth: 240 },
        { field: "jobName", headerName: "Job", flex: 2, minWidth: 220 },
        {
            field: "severity",
            headerName: "Severity",
            width: 120,
            cellRenderer: (params) => <SeverityBadge value={params.value} />,
        },
        {
            field: "endedAt",
            headerName: "Ended on",
            width: 150,
            valueFormatter: (params) => formatDate(params.value),
        },
        {
            // N8 settled 2026-08-17: both Expired and RemovedByUser reach this view, so this column
            // carries real information — it separates "this lapsed" from "you removed this".
            field: "removalReason",
            headerName: "Reason",
            width: 160,
            valueFormatter: (params) => REMOVAL_REASON_LABELS[params.value] || params.value || "",
        },
        {
            field: "note",
            headerName: "Original reason",
            flex: 1,
            minWidth: 170,
            tooltipField: "note",
            valueFormatter: (params) => params.value || "—",
        },
        {
            headerName: "Actions",
            sortable: false,
            width: 150,
            hide: !canChange,
            cellRenderer: (params) => (
                <ActionBar>
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => onReinstate(params.data)}
                    >
                        Re-enable
                    </button>
                </ActionBar>
            ),
        },
    ];
}
